import { useDialogueHighlight } from '../../hooks/useDialogueHighlight';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useQuery } from '@tanstack/react-query';
import { voiceSelectionService } from '../../services/voiceSelectApi';

interface PodcastDialogueProps {
  name: string;
  dialogue: string;
  index: number;
}

const PodcastDialogue = ({ name, dialogue, index }: PodcastDialogueProps) => {
  const { currentDialogueIndex, isPlaying } = useDialogueHighlight();
  const { dialogues } = useSelector((state: RootState) => state.podcastRes.data);
  
  // Fetch voices data
  const { data: voices = [] } = useQuery({
    queryKey: ['Voices'],
    queryFn: voiceSelectionService.getVoices,
  });
  
  // Determine if this dialogue is active
  const isActive = currentDialogueIndex === index;
  
  // Find the matching voice and get its image URL
  const voiceName = dialogues[index]?.voice_name;
  const matchedVoice = voices.find(voice => voice.name === voiceName);
  const voiceImageUrl = matchedVoice?.url || "https://img.freepik.com/premium-vector/avatar-profile-icon-flat-style-female-user-profile-vector-illustration-isolated-background-women-profile-sign-business-concept_157943-38866.jpg?semt=ais_hybrid";
  
  // Always show all dialogues until playback starts, then highlight active one
  const containerClasses = `max-w-screen-lg flex text-text-primary flex-col gap-3 transition-all duration-300 ${
    isPlaying 
      ? isActive 
        ? 'opacity-100 scale-100 border-l-4 border-[#168566] pl-2' 
        : 'opacity-30 scale-[0.98]'
      : 'opacity-100 scale-100'
  }`;

  return (
    <div className={containerClasses}>
      {/* name and avatar */}
      <div className="flex flex-row items-center justify-start gap-2">
        <div className="rounded-full">
          <img
            src={voiceImageUrl}
            alt={`${name} avatar`}
            className="rounded-full w-9 h-9 object-cover"
            onError={(e) => {
              // Fallback to default avatar if the voice image fails to load
              e.currentTarget.src = "https://img.freepik.com/premium-vector/avatar-profile-icon-flat-style-female-user-profile-vector-illustration-isolated-background-women-profile-sign-business-concept_157943-38866.jpg?semt=ais_hybrid";
            }}
          />
        </div>
        <h1 className={`text-left font-semibold text-[18px] ${isPlaying && isActive ? 'text-[#168566]' : ''}`}>
          {name}
        </h1>
      </div>
      {/* dialogue */}
      <div className="max-w-5xl">
        <p className="text-[26px] text-left font-normal text-text-primary">
          {dialogue}
        </p>
      </div>
    </div>
  );
};

export default PodcastDialogue;
